import { Component, Input, OnInit, OnChanges, SimpleChanges, ViewEncapsulation, ElementRef, ViewChild, AfterViewInit, OnDestroy, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as d3 from 'd3';
import { ThemeService } from '../../../../../core/services/theme.service';

export interface CylinderChartGroupItem {
  key?: string;
  value: number;
  color?: string;
}

export interface CylinderChartData {
  label: string;
  value?: number;
  bottomValue?: number;
  topValue?: number;
  values?: CylinderChartGroupItem[];
}

@Component({
  selector: 'app-cylinder-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './cylinder-chart.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None
})
export class CylinderChartComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() data: CylinderChartData[] = [];
  @Input() width: number = 400;
  @Input() height: number = 250;

  // Style Inputs
  @Input() barWidth: number = 24;
  @Input() borderRadius: number = 12;
  @Input() barColor: string = '#FBBF24'; // Orange-ish yellow as per mockup
  @Input() showGrid: boolean = true;
  @Input() animate: boolean = true;

  // New Inputs
  @Input() showYAxis: boolean = false;
  @Input() showTooltip: boolean = false;
  @Input() showValuesOnBars: boolean = true;
  @Input() showXAxisLine: boolean = false;
  @Input() showYAxisLine: boolean = false;
  @Input() xAxisTickFormat: any = null;
  @Input() xAxisShowOnlyFirstLast: boolean = false;
  @Input() hoverOpacity: number = 0.05;
  @Input() showBgRect: boolean = true;
  @Input() xAxisPadding: number = 0;
  @Input() innerPadding: number = 0.2;
  @Input() fitBarToBandwidth: boolean = false;
  @Input() maxBarWidth: number = 0;
  @Input() margins: any = { top: 40, right: 20, bottom: 40, left: 40 };

  // Legend
  @Input() showLegend: boolean = false;
  @Input() legendData: { label: string; color?: string }[] = [];
  @Input() legendGap: number = 100;
  @Input() legendPosition: 'left' | 'center' | 'right' = 'center';

  @ViewChild('chartContainer', { static: true }) chartContainer!: ElementRef;
  @ViewChild('tooltip', { static: false }) tooltipElement!: ElementRef;

  // Unique ID for gradients/patterns if needed
  private chartId = Math.random().toString(36).substring(2);
  private resizeObserver: ResizeObserver | null = null;
  private renderTimeout: any;

  // Actual dimensions used for D3
  private actualWidth: number = 400;
  private actualHeight: number = 250;

  constructor(private themeService: ThemeService) { }

  ngOnInit(): void { }

  ngAfterViewInit(): void {
    this.setupResizeObserver();
    this.renderChart();
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.renderTimeout) {
      clearTimeout(this.renderTimeout);
    }
  }

  private setupResizeObserver(): void {
    if (typeof ResizeObserver !== 'undefined' && this.chartContainer) {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.renderTimeout) clearTimeout(this.renderTimeout);
        this.renderTimeout = setTimeout(() => {
          this.renderChart();
        }, 100);
      });
      this.resizeObserver.observe(this.chartContainer.nativeElement);
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    if (this.renderTimeout) clearTimeout(this.renderTimeout);
    this.renderTimeout = setTimeout(() => {
      this.renderChart();
    }, 100);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['data'] || changes['showYAxis'] || changes['showTooltip'] || changes['xAxisPadding'] || changes['margins'] || changes['showLegend'] || changes['legendData'] || changes['innerPadding'] || changes['fitBarToBandwidth'] || changes['maxBarWidth']) && !changes['data']?.firstChange) {
      this.renderChart();
    }
  }

  private renderChart(): void {
    if (!this.chartContainer) return;

    const container = d3.select(this.chartContainer.nativeElement);
    container.selectAll('*').remove();

    if (!this.data || this.data.length === 0) return;

    const isDark = this.themeService.getTheme() === 'dark';
    const textColor = isDark ? '#a1a1aa' : '#6b7280';
    const valueColor = isDark ? '#f4f4f5' : '#1f2937';
    const gridColor = isDark ? 'rgba(71, 71, 78, 0.4)' : 'rgba(229, 231, 235, 0.6)';
    const trackColor = isDark ? 'rgba(251, 191, 36, 0.15)' : 'rgba(251, 191, 36, 0.2)';

    // Get current container dimensions
    const rect = this.chartContainer.nativeElement.getBoundingClientRect();
    this.actualWidth = rect.width || this.chartContainer.nativeElement.clientWidth || (typeof this.width === 'number' ? this.width : 400);
    this.actualHeight = rect.height || this.chartContainer.nativeElement.clientHeight || (typeof this.height === 'number' ? this.height : 300);

    // Use a fixed aspect ratio or dynamic depending on container
    // To prevent squashing, we ensure viewBox matches actual pixels
    const svg = container.append('svg')
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('viewBox', `0 0 ${this.actualWidth} ${this.actualHeight}`)
      .style('overflow', 'visible');

    const margin = this.margins || { top: 40, right: 20, bottom: 40, left: this.showYAxis ? 60 : 20 };
    const chartWidth = this.actualWidth - margin.left - margin.right;
    const chartHeight = this.actualHeight - margin.top - margin.bottom;

    if (this.showLegend) {
      this.appendLegends(svg, this.actualWidth, this.actualHeight, isDark);
    }

    const singleMaxValue = d3.max(this.data, d => (d.value || (d.bottomValue || 0) + (d.topValue || 0))) || 0;
    const isGrouped = this.data.some(d => d.values && d.values.length > 0);
    const groupedMaxValue = isGrouped ? (d3.max(this.data, d => d3.max(d.values || [], v => v.value) || 0) || 0) : 0;
    const maxValue = Math.max(singleMaxValue, groupedMaxValue);

    // We add some buffer so the highest bar isn't exactly touching the ceiling
    const upperLimit = maxValue * 1.15;

    const xScale = d3.scaleBand()
      .range([this.xAxisPadding, chartWidth - this.xAxisPadding])
      .padding(0.3)
      .domain(this.data.map(d => d.label));

    let xSubScale: d3.ScaleBand<string> | null = null;
    if (isGrouped) {
      const maxGroupsCount = d3.max(this.data, d => d.values?.length || 0) || 1;
      const subDomain = Array.from({ length: maxGroupsCount }, (_, i) => i.toString());
      xSubScale = d3.scaleBand()
        .domain(subDomain)
        .range([0, xScale.bandwidth()])
        .paddingInner(this.innerPadding);
    }

    // Calculate actual bar width if user wants overriding via input, 
    // but bounded by the bandwidth to prevent overlap
    let calculatedBarWidth = this.fitBarToBandwidth
      ? (isGrouped && xSubScale ? xSubScale.bandwidth() : xScale.bandwidth())
      : (isGrouped && xSubScale
        ? Math.min(this.barWidth, xSubScale.bandwidth())
        : Math.min(this.barWidth, xScale.bandwidth()));

    if (this.maxBarWidth > 0 && calculatedBarWidth > this.maxBarWidth) {
      if (isGrouped && this.fitBarToBandwidth && xSubScale) {
        const maxGroupsCount = d3.max(this.data, d => d.values?.length || 0) || 1;
        const desiredGroupWidth = Math.min(xScale.bandwidth(), this.maxBarWidth * maxGroupsCount);
        xSubScale.range([0, desiredGroupWidth]);
        calculatedBarWidth = xSubScale.bandwidth();
      } else {
        calculatedBarWidth = this.maxBarWidth;
      }
    }

    const yScale = d3.scaleLinear()
      .range([chartHeight, 0])
      .domain([0, upperLimit]);

    const chartGroup = svg.append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    // Axis Lines
    const axisColor = isDark ? 'rgba(161, 161, 170, 0.5)' : 'rgba(107, 114, 128, 0.5)';

    if (this.showYAxisLine) {
      chartGroup.append('line')
        .attr('x1', 0)
        .attr('y1', 0)
        .attr('x2', 0)
        .attr('y2', chartHeight)
        .attr('stroke', axisColor)
        .attr('stroke-width', 1);
    }

    if (this.showXAxisLine) {
      chartGroup.append('line')
        .attr('x1', 0)
        .attr('y1', chartHeight)
        .attr('x2', chartWidth)
        .attr('y2', chartHeight)
        .attr('stroke', axisColor)
        .attr('stroke-width', 1);
    }

    // Y Axis
    if (this.showYAxis) {
      const yAxis = d3.axisLeft(yScale)
        .ticks(5)
        .tickFormat(d => d.toLocaleString())
        .tickSize(0)
        .tickPadding(10);

      const yAxisG = chartGroup.append('g')
        .attr('class', 'y-axis')
        .call(yAxis);

      yAxisG.select('.domain').remove();
      yAxisG.selectAll('text')
        .attr('fill', textColor)
        .attr('font-size', '11px')
        .attr('font-weight', '500');
    }

    // Draw Grid (horizontal/vertical dashed lines in the background)
    if (this.showGrid) {
      // y-axis grid lines
      chartGroup.append('g')
        .attr('class', 'grid-lines')
        .call(d3.axisLeft(yScale)
          .tickSize(-chartWidth)
          .tickFormat(() => '') // No labels on left
          .ticks(5)
        )
        .selectAll('line')
        .attr('stroke', gridColor)
        .attr('stroke-dasharray', '3,3')
        .attr('stroke-width', 1);

      // x-axis grid lines (vertical)
      chartGroup.append('g')
        .attr('class', 'grid-lines')
        .attr('transform', `translate(0, ${chartHeight})`)
        .call(d3.axisBottom(xScale)
          .tickSize(-chartHeight)
          .tickFormat(() => '')
        )
        .selectAll('line')
        .attr('stroke', gridColor)
        .attr('stroke-dasharray', '3,3')
        .attr('stroke-width', 1);

      chartGroup.selectAll('.domain').remove();
    }

    // Draw bars
    const tooltip = d3.select(this.tooltipElement?.nativeElement);
    const mixColor = isDark ? '#f4f4f5' : '#ffffff';

    this.data.forEach((d) => {
      const startX = xScale(d.label) || 0;

      const columnGroup = chartGroup.append('g')
        .attr('class', 'column-group');

      // Setup tooltip events for the column group
      if (this.showTooltip) {
        columnGroup
          .on('mouseover', (event) => {
            const [mouseX, mouseY] = d3.pointer(event, container.node());
            const tooltipNode = tooltip.node() as HTMLElement;
            const tooltipWidth = tooltipNode.getBoundingClientRect().width;
            const containerWidth = container.node().clientWidth;

            let left = mouseX + 15;
            if (left + tooltipWidth > containerWidth) {
              left = mouseX - tooltipWidth - 15;
            }

            const tooltipHeight = tooltipNode.getBoundingClientRect().height;
            let top = mouseY - 15;
            if (top < 0) {
              top = mouseY + 15;
            } else if (top + tooltipHeight > container.node().clientHeight) {
              top = mouseY - tooltipHeight - 15;
            }

            tooltip.style('display', 'block')
              .style('left', `${left}px`)
              .style('top', `${top}px`);

            let html = `<div class="p-3 min-w-[180px] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl">
                            <div class="text-sm font-bold text-slate-900 dark:text-slate-100 mb-2 border-bottom pb-2 border-slate-100 dark:border-slate-700">${d.label} 2026</div>`;

            if (isGrouped && d.values) {
              d.values.forEach(v => {
                const label = v.key || 'Value';
                html += `<div class="flex items-center justify-between gap-4 mb-2">
                                <div class="flex items-center gap-2">
                                    <div class="w-2.5 h-2.5 rounded-full" style="background-color: ${v.color}"></div>
                                    <span class="text-xs text-slate-600 dark:text-slate-400 font-medium">${label}:</span>
                                </div>
                                <span class="text-xs font-bold text-slate-900 dark:text-slate-100">${v.value.toLocaleString()}</span>
                            </div>`;
              });
            } else {
              html += `<div class="flex items-center justify-between gap-4">
                            <span class="text-xs text-slate-600 dark:text-slate-400 font-medium">Value:</span>
                            <span class="text-xs font-bold text-slate-900 dark:text-slate-100">${(d.value || 0).toLocaleString()}</span>
                        </div>`;
            }
            html += `</div>`;
            tooltip.html(html);
          })
          .on('mousemove', (event) => {
            const [mouseX, mouseY] = d3.pointer(event, container.node());
            const tooltipNode = tooltip.node() as HTMLElement;
            const tooltipWidth = tooltipNode.getBoundingClientRect().width;
            const containerWidth = container.node().clientWidth;

            let left = mouseX + 15;
            if (left + tooltipWidth > containerWidth) {
              left = mouseX - tooltipWidth - 15;
            }

            const tooltipHeight = tooltipNode.getBoundingClientRect().height;
            let top = mouseY - 15;
            if (top < 0) {
              top = mouseY + 15;
            } else if (top + tooltipHeight > container.node().clientHeight) {
              top = mouseY - tooltipHeight - 15;
            }

            tooltip.style('left', `${left}px`)
              .style('top', `${top}px`);
          })
          .on('mouseout', () => {
            tooltip.style('display', 'none');
          });
      }

      if (isGrouped && d.values && d.values.length > 0 && xSubScale) {
        const groupWidth = xSubScale.range()[1];
        const groupStartX = (xScale.bandwidth() - groupWidth) / 2;

        d.values.forEach((v, idx) => {
          const subXPos = groupStartX + (xSubScale?.(idx.toString()) || 0) + (xSubScale!.bandwidth() - calculatedBarWidth) / 2;
          this.drawCylinderInGroup(columnGroup, startX + subXPos, v.value, v.color || this.barColor, yScale, chartHeight, calculatedBarWidth, upperLimit, textColor, valueColor, mixColor);
        });
      } else if (d.bottomValue !== undefined || d.topValue !== undefined) {
        const xPos = (xScale.bandwidth() - calculatedBarWidth) / 2;
        const b = d.bottomValue || 0;
        const t = d.topValue || 0;
        this.drawCylinderInGroup(columnGroup, startX + xPos, b, this.barColor, yScale, chartHeight, calculatedBarWidth, upperLimit, textColor, valueColor, mixColor);
        const topYScale = d3.scaleLinear().domain([0, upperLimit]).range([chartHeight - (yScale(0) - yScale(b)), - (yScale(0) - yScale(b))]);
        this.drawCylinderInGroup(columnGroup, startX + xPos, t, this.barColor + '66', topYScale, chartHeight, calculatedBarWidth, upperLimit, textColor, valueColor, mixColor);
      } else {
        const xPos = (xScale.bandwidth() - calculatedBarWidth) / 2;
        this.drawCylinderInGroup(columnGroup, startX + xPos, d.value || 0, this.barColor, yScale, chartHeight, calculatedBarWidth, upperLimit, textColor, valueColor, mixColor);
      }

      // Add a transparent rectangle to capture mouse events for the whole column width
      if (this.showTooltip) {
        const bgRect = columnGroup.append('rect')
          .attr('x', startX)
          .attr('y', 0)
          .attr('width', xScale.bandwidth())
          .attr('height', chartHeight)
          .attr('fill', isDark ? '#ffffff' : '#000000')
          .attr('fill-opacity', this.showBgRect ? this.hoverOpacity : 0)
          .attr('pointer-events', 'all')
          .attr('rx', 4)
          .style('display', this.showBgRect ? 'block' : 'none');

        columnGroup.on('mouseover.highlight', () => {
          if (this.showBgRect) {
            bgRect.transition().duration(200).attr('fill-opacity', this.hoverOpacity * 2.5);
          }
        });
        columnGroup.on('mouseout.highlight', () => {
          if (this.showBgRect) {
            bgRect.transition().duration(200).attr('fill-opacity', this.hoverOpacity);
          }
        });
      }
    });

    // X Axis Labels
    const xAxis = d3.axisBottom(xScale).tickSize(0).tickPadding(15);
    
    if (this.xAxisShowOnlyFirstLast) {
      const domain = xScale.domain();
      if (domain.length > 1) {
        xAxis.tickValues([domain[0], domain[domain.length - 1]]);
      }
    }

    const xAxisGroup = chartGroup.append('g')
      .attr('transform', `translate(0, ${chartHeight})`)
      .call(xAxis)
      .select('.domain').remove();

    chartGroup.selectAll('.tick text')
      .attr('fill', textColor)
      .attr('font-size', '11px')
      .attr('font-family', 'Inter, sans-serif')
      .attr('font-weight', '500');
  }

  private appendLegends(svg: d3.Selection<any, any, any, any>, width: number, height: number, isDark: boolean) {
    if (!this.legendData || this.legendData.length === 0) return;

    const legendGroup = svg.append('g').attr('class', 'legend-container');
    const spacing = this.legendGap; // Using legendGap input for space between legend items
    const circleRadius = 5;
    const textPadding = 12; // Space between circle and text
    
    let totalLegendsWidth = 0;
    const itemWidths: number[] = [];

    // Temporary group to measure text widths
    const tempG = svg.append('g').attr('opacity', 0);
    
    this.legendData.forEach((d, i) => {
      const text = tempG.append('text')
        .style('font-size', '12px')
        .text(d.label);
      
      const node = text.node() as SVGTextElement;
      let textWidth = 0;
      if (node) {
        try {
          textWidth = node.getBBox().width;
        } catch (e) {
          textWidth = d.label.length * 7;
        }
      }
      
      const itemWidth = circleRadius * 2 + textPadding + textWidth;
      itemWidths.push(itemWidth);
      totalLegendsWidth += itemWidth + (i < this.legendData.length - 1 ? spacing : 0);
    });

    tempG.remove();

    let lx = (width - totalLegendsWidth) / 2;
    if (this.legendPosition === 'left') lx = this.margins.left || 30;
    else if (this.legendPosition === 'right') lx = width - totalLegendsWidth - (this.margins.right || 20);

    let currentX = lx;
    this.legendData.forEach((d, i) => {
      const g = legendGroup.append('g')
        .attr('transform', `translate(${currentX}, ${height - 12})`);
      
      g.append('circle')
        .attr('cx', circleRadius)
        .attr('cy', 6)
        .attr('r', circleRadius)
        .attr('fill', d.color || (isDark ? '#a1a1aa' : '#64748b'));

      g.append('text')
        .attr('x', circleRadius * 2 + textPadding)
        .attr('y', 10)
        .text(d.label)
        .style('font-size', '12px')
        .style('fill', isDark ? '#a1a1aa' : '#64748b')
        .style('font-weight', '500');
      
      currentX += itemWidths[i] + spacing;
    });
  }

  private drawCylinderInGroup(group: d3.Selection<SVGGElement, unknown, null, undefined>, xPos: number, val: number, colorToUse: string, yScale: d3.ScaleLinear<number, number>, chartHeight: number, calculatedBarWidth: number, upperLimit: number, textColor: string, valueColor: string, mixColor: string): void {
    const yPos = Math.max(0, yScale(val));
    const barHeight = Math.max(0, yScale(0) - yPos);
    const bottomY = yScale(0);
    const rx = calculatedBarWidth / 2;
    const ry = rx * 0.4;

    const bodyFill = colorToUse;
    const topInterpolation = d3.interpolate(mixColor, colorToUse);
    const topFill = topInterpolation(0.4);

    const trackInterpolation = d3.interpolate(mixColor, colorToUse);
    const trackBodyFill = trackInterpolation(0.15);
    const trackTopFill = trackInterpolation(0.08);

    const barGroup = group.append('g')
      .attr('transform', `translate(${xPos}, 0)`);

    // 1. Background Track
    barGroup.append('rect')
      .attr('y', 0)
      .attr('width', calculatedBarWidth)
      .attr('height', chartHeight)
      .attr('fill', trackBodyFill);

    barGroup.append('ellipse')
      .attr('cx', rx)
      .attr('cy', chartHeight)
      .attr('rx', rx)
      .attr('ry', ry)
      .attr('fill', trackBodyFill);

    barGroup.append('ellipse')
      .attr('cx', rx)
      .attr('cy', 0)
      .attr('rx', rx)
      .attr('ry', ry)
      .attr('fill', trackTopFill);

    // 2. Foreground Bar
    const fgGroup = barGroup.append('g');

    fgGroup.append('ellipse')
      .attr('cx', rx)
      .attr('cy', bottomY)
      .attr('rx', rx)
      .attr('ry', ry)
      .attr('fill', bodyFill);

    const rect = fgGroup.append('rect')
      .attr('y', this.animate ? bottomY : yPos)
      .attr('width', calculatedBarWidth)
      .attr('height', this.animate ? 0 : barHeight)
      .attr('fill', bodyFill);

    const topFace = fgGroup.append('ellipse')
      .attr('cx', rx)
      .attr('cy', this.animate ? bottomY : yPos)
      .attr('rx', rx)
      .attr('ry', ry)
      .attr('fill', topFill);

    if (this.animate) {
      rect.transition()
        .duration(800)
        .ease(d3.easeCubicOut)
        .attr('y', yPos)
        .attr('height', barHeight);

      topFace.transition()
        .duration(800)
        .ease(d3.easeCubicOut)
        .attr('cy', yPos);
    }

    // 3. Value Label
    if (this.showValuesOnBars) {
      const labelYOffset = val <= upperLimit * 0.1 ? -15 : 15;
      const valueLabel = fgGroup.append('text')
        .attr('x', rx)
        .attr('y', yPos + labelYOffset)
        .attr('text-anchor', 'middle')
        .attr('fill', val <= upperLimit * 0.1 ? textColor : valueColor)
        .attr('font-size', '13px')
        .attr('font-weight', '600')
        .attr('opacity', this.animate ? 0 : 1)
        .text(val.toLocaleString());

      if (this.animate) {
        valueLabel.transition()
          .duration(800)
          .ease(d3.easeCubicOut)
          .attr('opacity', 1);
      }
    }
  }
}
